import { useEffect, useState, useCallback } from 'react';
import { getRiskMetrics } from '../api/portfolio';
import type { RiskMetricsData } from '../api/portfolio';

interface UseRiskMetricsResult {
  data: RiskMetricsData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch risk-adjusted performance metrics (Sharpe, Sortino, Max Drawdown, Calmar).
 * Memoised on [userId, range].
 */
export function useRiskMetrics(
  userId: string | undefined,
  range: '30d' | '90d' | '1y',
): UseRiskMetricsResult {
  const [data, setData] = useState<RiskMetricsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await getRiskMetrics(userId, range);
      setData(result);
    } catch {
      setError('Failed to load risk metrics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [userId, range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
